import { ReactNode } from 'react';
import { UtilisationBar } from './UtilisationBar';
import {
  TestCentreOverviewDateDto,
  TestCentreOverviewDistrictDto,
  TestCentreOverviewLrwPairingDto,
  TestCentreOverviewSpeakingSessionDto,
} from 'api/planning';
import { Time } from '@britishcouncil/react-common';

type ColumnKey =
  | 'date'
  | 'speakingVenue'
  | 'speakingPlannedSlots'
  | 'speakingBookedSlots'
  | 'speakingOsrBookedSlots'
  | 'speakingExaminersAssigned'
  | 'speakingExaminersUnassigned'
  | 'lrwVenue'
  | 'lrwSessionTime'
  | 'lrwPlannedSeats'
  | 'lrwBookedSeats'
  | 'lrwAcBookedSeats'
  | 'lrwGtBookedSeats'
  | 'lrwOsrBookedSeats'
  | 'confirmedRegistrations'
  | 'unconfirmedRegistrations'
  | 'utilisation';

export const COLUMN_ORDER: ColumnKey[] = [
  'date',
  'speakingVenue',
  'speakingPlannedSlots',
  'speakingBookedSlots',
  'speakingOsrBookedSlots',
  'speakingExaminersAssigned',
  'speakingExaminersUnassigned',
  'lrwVenue',
  'lrwSessionTime',
  'lrwPlannedSeats',
  'lrwBookedSeats',
  'lrwAcBookedSeats',
  'lrwGtBookedSeats',
  'lrwOsrBookedSeats',
  'confirmedRegistrations',
  'unconfirmedRegistrations',
  'utilisation',
];

const ROW_SPANNED_COLUMN_KEYS: ColumnKey[] = [
  'date',
  'speakingVenue',
  'speakingPlannedSlots',
  'speakingBookedSlots',
  'speakingOsrBookedSlots',
  'speakingExaminersAssigned',
  'speakingExaminersUnassigned',
];

export const isRowSpannedColumn = (key: ColumnKey): boolean =>
  ROW_SPANNED_COLUMN_KEYS.includes(key);

export const getDateCellContent = (key: ColumnKey, date: TestCentreOverviewDateDto): ReactNode => {
  switch (key) {
    case 'date':
      return date.date;
    case 'speakingPlannedSlots':
      return date.speakingPlannedSlots;
    case 'speakingBookedSlots':
      return date.speakingBookedSlots;
    case 'speakingOsrBookedSlots':
      return date.speakingOsrBookedSlots;
    case 'speakingExaminersAssigned':
      return date.speakingExaminersAssigned;
    case 'speakingExaminersUnassigned':
      return date.speakingExaminersUnassigned;
    case 'lrwPlannedSeats':
      return date.lrwPlannedSeats;
    case 'lrwBookedSeats':
      return date.lrwBookedSeats;
    case 'lrwAcBookedSeats':
      return date.lrwAcBookedSeats;
    case 'lrwGtBookedSeats':
      return date.lrwGtBookedSeats;
    case 'lrwOsrBookedSeats':
      return date.lrwOsrBookedSeats;
    case 'confirmedRegistrations':
      return date.confirmedRegistrations;
    case 'unconfirmedRegistrations':
      return date.unconfirmedRegistrations;
    case 'utilisation':
      return <UtilisationBar value={date.utilisation} />;
    case 'speakingVenue':
    case 'lrwVenue':
    case 'lrwSessionTime':
      return '';
  }
};

export const getDistrictCellContent = (
  key: ColumnKey,
  district: TestCentreOverviewDistrictDto
): ReactNode => {
  switch (key) {
    case 'date':
      return district.districtName;
    case 'speakingPlannedSlots':
      return district.speakingPlannedSlots;
    case 'speakingBookedSlots':
      return district.speakingBookedSlots;
    case 'speakingOsrBookedSlots':
      return district.speakingOsrBookedSlots;
    case 'speakingExaminersAssigned':
      return district.speakingExaminersAssigned;
    case 'speakingExaminersUnassigned':
      return district.speakingExaminersUnassigned;
    case 'lrwPlannedSeats':
      return district.lrwPlannedSeats;
    case 'lrwBookedSeats':
      return district.lrwBookedSeats;
    case 'lrwAcBookedSeats':
      return district.lrwAcBookedSeats;
    case 'lrwGtBookedSeats':
      return district.lrwGtBookedSeats;
    case 'lrwOsrBookedSeats':
      return district.lrwOsrBookedSeats;
    case 'confirmedRegistrations':
      return district.confirmedRegistrations;
    case 'unconfirmedRegistrations':
      return district.unconfirmedRegistrations;
    case 'utilisation':
      return <UtilisationBar value={district.utilisation} />;
    case 'speakingVenue':
    case 'lrwVenue':
    case 'lrwSessionTime':
      return '';
  }
};

export const getSpeakingSessionCellContent = (
  key: ColumnKey,
  speakingSession: TestCentreOverviewSpeakingSessionDto | undefined
): ReactNode => {
  switch (key) {
    case 'speakingVenue':
      return speakingSession?.venueName ?? '';
    case 'speakingPlannedSlots':
      return speakingSession?.plannedSlots ?? '';
    case 'speakingBookedSlots':
      return speakingSession?.bookedSlots ?? '';
    case 'speakingOsrBookedSlots':
      return speakingSession?.osrBookedSlots ?? '';
    case 'speakingExaminersAssigned':
      return speakingSession?.examinersAssigned ?? '';
    case 'speakingExaminersUnassigned':
      return speakingSession?.examinersUnassigned ?? '';
    default:
      return '';
  }
};

export const getLrwPairingCellContent = (
  key: ColumnKey,
  pairing: TestCentreOverviewLrwPairingDto
): ReactNode => {
  const { lrwSession } = pairing;
  switch (key) {
    case 'lrwVenue':
      return lrwSession?.venueName ?? '';
    case 'lrwSessionTime':
      return lrwSession ? formatLrwSessionTime(lrwSession.startTime, lrwSession.endTime) : '';
    case 'lrwPlannedSeats':
      return lrwSession?.plannedSeats ?? '';
    case 'lrwBookedSeats':
      return lrwSession?.bookedSeats ?? '';
    case 'lrwAcBookedSeats':
      return lrwSession?.acBookedSeats ?? '';
    case 'lrwGtBookedSeats':
      return lrwSession?.gtBookedSeats ?? '';
    case 'lrwOsrBookedSeats':
      return lrwSession?.osrBookedSeats ?? '';
    case 'confirmedRegistrations':
      return pairing.confirmedRegistrations;
    case 'unconfirmedRegistrations':
      return pairing.unconfirmedRegistrations;
    case 'utilisation':
      return <UtilisationBar value={pairing.utilisation} />;
    default:
      return '';
  }
};

const formatLrwSessionTime = (startTime: string, endTime: string): string =>
  `${Time.fromString(startTime)} - ${Time.fromString(endTime)}`;
